// EventPicker.tsx — pick / create / end a research_event from the mobile capture sheet (P3 events).
//
// Lets an Investment-team user tag a capture to an EVENT (and a conference SUB-EVENT):
//   • lists their ACTIVE research_events (status=active, recent) with sub-events nested;
//   • tapping one selects it; "Clear" unselects;
//   • "Create event" (name + type) and, when an event is selected, "Create sub-event"
//     (parent_event_id = selected) via the research-event-create edge fn;
//   • "End event" (with confirm) on the selected event → research_end_event RPC (fires the digest).
//
// Offline resilience: the active-events list is read through useCachedQuery (cacheKey
// mobile.events.active) so it renders from cache offline (read-only). Every WRITE (create / end)
// needs the network → those affordances disable with a clear "needs a connection" note when offline.
//
// Types: research_events / its RPCs are not in the generated Supabase types yet, so the table read,
// the functions.invoke, and the rpc all go through `as any` (matching the createMobileSource idiom).

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  X,
  WifiOff,
  Plus,
  Check,
  Loader2,
  CircleSlash,
  CornerDownRight,
  Square,
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useToast } from '@/hooks/use-toast';
import { useIsOnline } from '@/hooks/useIsOnline';
import { useCachedQuery } from '@/lib/offline/cachedQuery';
import { supabase } from '@/integrations/supabase/client';
import type { StickyEvent } from '@/lib/capture/eventSelection';

export type ResearchEventType = 'conference' | 'roadshow' | 'one_to_one' | 'bdm';

const EVENT_TYPES: Array<{ value: ResearchEventType; label: string }> = [
  { value: 'conference', label: 'Conference' },
  { value: 'roadshow', label: 'Roadshow' },
  { value: 'one_to_one', label: 'One-to-one' },
  { value: 'bdm', label: 'BDM' },
];

/** A row from research_events (subset we select). */
export interface ResearchEventRow {
  id: string;
  name: string;
  type: ResearchEventType;
  parent_event_id: string | null;
  status: 'active' | 'ended';
  starts_at: string | null;
  ends_at: string | null;
  created_at: string | null;
}

interface EventNode extends ResearchEventRow {
  children: ResearchEventRow[];
}

async function fetchActiveEvents(): Promise<ResearchEventRow[]> {
  // research_events isn't in the generated types — cast the builder. RLS already scopes the read to
  // events the user can see (has_research_capability(read)); we only filter to ACTIVE + recent here.
  const { data, error } = await (supabase as any)
    .from('research_events')
    .select('id, name, type, parent_event_id, status, starts_at, ends_at, created_at')
    .eq('status', 'active')
    .order('created_at', { ascending: false })
    .limit(50);
  if (error) throw new Error(error.message);
  return (data ?? []) as ResearchEventRow[];
}

/** Group rows into top-level events with their sub-events nested underneath (one level deep).
 *  A sub-event whose parent isn't in the fetched set (e.g. the parent was ended while the child
 *  is still active, or a partial end) is PROMOTED to top-level rather than silently dropped, so no
 *  active event is ever invisible/unselectable in the picker. */
function nest(rows: ResearchEventRow[]): EventNode[] {
  const presentIds = new Set(rows.map((r) => r.id));
  // A row is "top-level" if it has no parent, OR its parent isn't among the fetched rows (orphaned).
  const tops = rows.filter((r) => !r.parent_event_id || !presentIds.has(r.parent_event_id));
  const childrenByParent = new Map<string, ResearchEventRow[]>();
  for (const r of rows) {
    if (r.parent_event_id && presentIds.has(r.parent_event_id)) {
      const arr = childrenByParent.get(r.parent_event_id) ?? [];
      arr.push(r);
      childrenByParent.set(r.parent_event_id, arr);
    }
  }
  return tops.map((t) => ({ ...t, children: childrenByParent.get(t.id) ?? [] }));
}

const OFFLINE_WRITE_NOTE = 'Creating or ending an event needs a connection.';

export interface EventPickerProps {
  /** Currently-selected sticky event, or null when none. */
  selected: StickyEvent | null;
  /** Select (or, with null, clear) the sticky event. */
  onSelect: (event: StickyEvent | null) => void;
  /** Close the picker. */
  onClose: () => void;
}

export function EventPicker({ selected, onSelect, onClose }: EventPickerProps) {
  const { toast } = useToast();
  const online = useIsOnline();

  const {
    data: events,
    isFromCache,
    isLoading,
  } = useCachedQuery<ResearchEventRow[]>({
    queryKey: ['mobile', 'events', 'active'],
    queryFn: fetchActiveEvents,
    cacheKey: 'mobile.events.active',
  });

  const tree = useMemo(() => nest(events ?? []), [events]);

  // Stale-sticky reconciliation: if the selected sticky event was ended elsewhere (another device,
  // or server-side digest auto-close), it won't be in the active list (fetchActiveEvents filters
  // status=active). Without this, every new capture would silently tag an already-ended event. Once
  // we have a LIVE list (loaded, not from cache) that doesn't contain the selection, drop the sticky
  // tag. Gate on a live read only — never clear from cached/placeholder data (offline we keep showing
  // the last-known tag rather than dropping it on a stale-but-real selection). Reconcile once per
  // resolved live list so the clear can't fight a user re-selecting the same id.
  const reconciledKeyRef = useRef<string | null>(null);
  useEffect(() => {
    if (isLoading || isFromCache || !events) return;
    const selectedId = selected?.eventId ?? null;
    // Key the pass on (selectedId + which ids are present) so a fresh list re-evaluates.
    const listKey = `${selectedId ?? '∅'}|${events.map((e) => e.id).join(',')}`;
    if (reconciledKeyRef.current === listKey) return;
    reconciledKeyRef.current = listKey;
    if (selectedId && !events.some((e) => e.id === selectedId)) {
      onSelect(null);
      toast({
        title: 'Event no longer active',
        description: `“${selected?.eventName ?? 'That event'}” has ended — captures are untagged.`,
      });
    }
  }, [events, isFromCache, isLoading, onSelect, selected?.eventId, selected?.eventName, toast]);

  // Create-event form state.
  const [newName, setNewName] = useState('');
  const [newType, setNewType] = useState<ResearchEventType>('conference');
  const [creating, setCreating] = useState(false);
  // Whether the create form targets a sub-event of the selected event vs. a top-level event.
  const [asSubEvent, setAsSubEvent] = useState(false);
  const [ending, setEnding] = useState(false);
  const [confirmEnd, setConfirmEnd] = useState(false);

  const handlePick = useCallback(
    (row: ResearchEventRow) => {
      onSelect({ eventId: row.id, eventName: row.name });
      onClose();
    },
    [onSelect, onClose],
  );

  const handleCreate = useCallback(async () => {
    const name = newName.trim();
    if (!name) {
      toast({ title: 'Name required', description: 'Give the event a name first.' });
      return;
    }
    if (!online) {
      toast({ title: 'You are offline', description: OFFLINE_WRITE_NOTE, variant: 'destructive' });
      return;
    }
    const parentEventId = asSubEvent ? selected?.eventId ?? null : null;
    setCreating(true);
    try {
      const { data, error } = await supabase.functions.invoke('research-event-create', {
        body: {
          name,
          type: newType,
          parent_event_id: parentEventId,
        },
      });
      if (error) throw error;
      const eventId =
        (data as any)?.event_id ?? (data as any)?.eventId ?? (data as any)?.id ?? null;
      if (!eventId) throw new Error('Event created but no id was returned.');
      // Newly created event/sub-event becomes the selected sticky event.
      onSelect({ eventId: eventId as string, eventName: name });
      toast({
        title: asSubEvent ? 'Sub-event created' : 'Event created',
        description: `“${name}” is now your active event.`,
      });
      setNewName('');
      onClose();
    } catch (e) {
      toast({
        title: 'Could not create event',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setCreating(false);
    }
  }, [asSubEvent, newName, newType, online, onClose, onSelect, selected?.eventId, toast]);

  const handleEnd = useCallback(async () => {
    if (!selected) return;
    if (!online) {
      toast({ title: 'You are offline', description: OFFLINE_WRITE_NOTE, variant: 'destructive' });
      return;
    }
    setEnding(true);
    try {
      const { error } = await (supabase.rpc as any)('research_end_event', {
        _event_id: selected.eventId,
      });
      if (error) throw new Error(error.message);
      onSelect(null);
      toast({ title: 'Event ended', description: 'Digest generating.' });
      onClose();
    } catch (e) {
      toast({
        title: 'Could not end event',
        description: e instanceof Error ? e.message : 'Please try again.',
        variant: 'destructive',
      });
    } finally {
      setEnding(false);
      setConfirmEnd(false);
    }
  }, [online, onClose, onSelect, selected, toast]);

  return (
    <div className="flex flex-col gap-3">
      <div className="flex items-center justify-between">
        <h3 className="font-heading text-sm font-semibold">Tag to an event</h3>
        <button
          type="button"
          aria-label="Close event picker"
          onClick={onClose}
          className="rounded-full p-1 text-muted-foreground hover:bg-accent"
        >
          <X className="h-5 w-5" />
        </button>
      </div>

      {!online && (
        <div
          role="status"
          aria-live="polite"
          className="flex items-start gap-2 rounded-lg border border-amber-300/60 bg-amber-50 p-2.5 text-[11px] leading-snug text-amber-900 dark:border-amber-500/30 dark:bg-amber-950/40 dark:text-amber-200"
        >
          <WifiOff className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
          <span>
            You&apos;re offline. You can still pick from your saved events, but {OFFLINE_WRITE_NOTE.toLowerCase()}
          </span>
        </div>
      )}

      {/* Current selection + clear */}
      <div className="flex items-center justify-between rounded-lg border border-border bg-muted/30 px-3 py-2">
        <span className="text-xs">
          {selected ? (
            <>
              <span className="text-muted-foreground">Selected: </span>
              <span className="font-medium">{selected.eventName}</span>
            </>
          ) : (
            <span className="text-muted-foreground">No event selected</span>
          )}
        </span>
        {selected && (
          <button
            type="button"
            onClick={() => onSelect(null)}
            className="flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] text-muted-foreground hover:bg-accent"
          >
            <CircleSlash className="h-3 w-3" aria-hidden="true" /> Clear
          </button>
        )}
      </div>

      {/* Active events list */}
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <Label className="text-xs text-muted-foreground">Active events</Label>
          {isFromCache && (
            <span className="flex items-center gap-1 text-[10px] text-amber-700 dark:text-amber-300">
              <WifiOff className="h-2.5 w-2.5" aria-hidden="true" /> saved
            </span>
          )}
        </div>

        {isLoading && tree.length === 0 ? (
          <div className="flex items-center gap-2 px-1 py-3 text-xs text-muted-foreground">
            <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading events…
          </div>
        ) : tree.length === 0 ? (
          <p className="px-1 py-2 text-xs text-muted-foreground">
            {!online
              ? 'No saved events available offline — reconnect to load your events.'
              : 'No active events yet. Create one below.'}
          </p>
        ) : (
          <ul className="max-h-44 overflow-y-auto rounded-lg border border-border">
            {tree.map((node) => (
              <li key={node.id}>
                <EventRow
                  row={node}
                  isSelected={selected?.eventId === node.id}
                  onPick={handlePick}
                />
                {node.children.map((child) => (
                  <EventRow
                    key={child.id}
                    row={child}
                    nested
                    isSelected={selected?.eventId === child.id}
                    onPick={handlePick}
                  />
                ))}
              </li>
            ))}
          </ul>
        )}
      </div>

      {/* Create event / sub-event */}
      <div className="flex flex-col gap-2 rounded-lg border border-border p-3">
        <div className="flex items-center gap-2">
          <Plus className="h-4 w-4 text-primary" aria-hidden="true" />
          <span className="text-xs font-medium">
            {asSubEvent && selected ? 'Create sub-event' : 'Create event'}
          </span>
        </div>

        {selected && (
          <label className="flex items-center gap-2 text-[11px] text-muted-foreground">
            <input
              type="checkbox"
              checked={asSubEvent}
              onChange={(e) => setAsSubEvent(e.target.checked)}
              className="h-3.5 w-3.5 accent-primary"
            />
            Make it a sub-event of “{selected.eventName}”
          </label>
        )}

        <Input
          aria-label="Event name"
          placeholder="Event name"
          value={newName}
          onChange={(e) => setNewName(e.target.value)}
        />

        <div className="flex flex-wrap gap-1.5">
          {EVENT_TYPES.map((t) => (
            <button
              key={t.value}
              type="button"
              onClick={() => setNewType(t.value)}
              className={`rounded-full px-2.5 py-1 text-[11px] ${
                newType === t.value
                  ? 'bg-primary text-primary-foreground'
                  : 'bg-muted text-muted-foreground hover:bg-accent'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        <Button
          type="button"
          onClick={() => void handleCreate()}
          disabled={!newName.trim() || creating || !online}
          className="w-full"
        >
          {creating ? (
            <Loader2 className="h-4 w-4 animate-spin" />
          ) : !online ? (
            'Offline — needs a connection'
          ) : asSubEvent && selected ? (
            'Create sub-event'
          ) : (
            'Create event'
          )}
        </Button>
      </div>

      {/* End event */}
      {selected && (
        <div className="flex flex-col gap-2 rounded-lg border border-destructive/30 bg-destructive/5 p-3">
          <div className="flex items-center gap-2">
            <Square className="h-4 w-4 text-destructive" aria-hidden="true" />
            <span className="text-xs font-medium">End “{selected.eventName}”</span>
          </div>
          <p className="text-[11px] text-muted-foreground">
            Closes this event (and its sub-events) and generates the digest. {!online && OFFLINE_WRITE_NOTE}
          </p>
          {confirmEnd ? (
            <div className="flex gap-2">
              <Button
                type="button"
                variant="destructive"
                onClick={() => void handleEnd()}
                disabled={ending || !online}
                className="flex-1"
              >
                {ending ? <Loader2 className="h-4 w-4 animate-spin" /> : 'Confirm end'}
              </Button>
              <Button
                type="button"
                variant="outline"
                onClick={() => setConfirmEnd(false)}
                disabled={ending}
                className="flex-1"
              >
                Cancel
              </Button>
            </div>
          ) : (
            <Button
              type="button"
              variant="outline"
              onClick={() => setConfirmEnd(true)}
              disabled={!online}
              className="w-full border-destructive/40 text-destructive hover:bg-destructive/10"
            >
              End event
            </Button>
          )}
        </div>
      )}
    </div>
  );
}

interface EventRowProps {
  row: ResearchEventRow;
  isSelected: boolean;
  nested?: boolean;
  onPick: (row: ResearchEventRow) => void;
}

function EventRow({ row, isSelected, nested, onPick }: EventRowProps) {
  const typeLabel = EVENT_TYPES.find((t) => t.value === row.type)?.label ?? row.type;
  return (
    <button
      type="button"
      onClick={() => onPick(row)}
      className={`flex w-full items-center gap-2 border-b border-border px-3 py-2 text-left last:border-b-0 ${
        nested ? 'pl-7' : ''
      } ${isSelected ? 'bg-primary/10' : 'hover:bg-accent'}`}
    >
      {nested && <CornerDownRight className="h-3 w-3 shrink-0 text-muted-foreground" aria-hidden="true" />}
      <span className="flex-1 truncate text-xs font-medium">{row.name}</span>
      <span className="shrink-0 rounded-full bg-muted px-1.5 py-0.5 text-[10px] text-muted-foreground">
        {typeLabel}
      </span>
      {isSelected && <Check className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />}
    </button>
  );
}
